import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";

import { getConversationRenders, retryRender } from "@/api";
import { isAxiosNotFoundError } from "@/api/errors";
import type { ITemplateRenderSummary } from "@/api/template-renders/template-renders.types";

export const LIVE_STATUSES = new Set(["pending", "processing"]);

const POLL_INTERVAL_MS = 5000;
// Ceiling for the error-branch exponential backoff (see makeRenderPollInterval): a
// sustained outage must not be polled every 5s forever by every mounted tab.
const MAX_ERROR_INTERVAL_MS = 5 * 60_000;

export const templateRendersQueryKey = (conversationId: number) =>
    ["template-renders", conversationId] as const;

type RenderPollState = {
    status: "pending" | "error" | "success";
    error: unknown;
    data: ITemplateRenderSummary[] | undefined;
    errorUpdateCount: number;
};

export function renderPollInterval(
    state: RenderPollState,
    consecutiveErrors: number
): number | false {
    if (state.status === "error") {
        // A 404 means the renders/conversation are gone — stop. Other errors are
        // transient: keep polling, but exponentially back off so a hard-down endpoint
        // isn't hammered every 5s by every tab.
        if (isAxiosNotFoundError(state.error)) return false;
        const backoff =
            POLL_INTERVAL_MS * 2 ** Math.max(0, consecutiveErrors - 1);
        return Math.min(backoff, MAX_ERROR_INTERVAL_MS);
    }
    const renders = state.data ?? [];
    const live = renders.some((r) => LIVE_STATUSES.has(r.status));
    return live ? POLL_INTERVAL_MS : false;
}

// The global QueryClient uses retry:false, and React Query resets fetchFailureCount to 0 at
// the start of every fetch — so across polls it is stuck at 1 and cannot drive a backoff.
// errorUpdateCount is monotonic (one increment per failed fetch), so consecutive failures =
// errorUpdateCount minus its value at the last non-error state; the returned closure holds
// that baseline across poll cycles and resets it the moment a poll succeeds.
export function makeRenderPollInterval() {
    let errorBaseline = 0;
    return (state: RenderPollState): number | false => {
        if (state.status !== "error") {
            errorBaseline = state.errorUpdateCount;
        }
        const consecutiveErrors = Math.max(
            0,
            state.errorUpdateCount - errorBaseline
        );
        return renderPollInterval(state, consecutiveErrors);
    };
}

export function useTemplateRenders(conversationId: number) {
    const pollRef = useRef<ReturnType<typeof makeRenderPollInterval> | null>(
        null
    );
    if (!pollRef.current) {
        pollRef.current = makeRenderPollInterval();
    }
    return useQuery({
        queryKey: templateRendersQueryKey(conversationId),
        queryFn: () => getConversationRenders(conversationId),
        refetchInterval: (query) => pollRef.current!(query.state),
    });
}

export function useRetryRender(conversationId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (renderId: number) => retryRender(conversationId, renderId),
        onSuccess: (updated, renderId) => {
            // Reflect the retried render as pending at once so the Retry button can't
            // re-fire in the gap before the invalidated read lands.
            queryClient.setQueryData<ITemplateRenderSummary[]>(
                templateRendersQueryKey(conversationId),
                (current) =>
                    current?.map((r) => (r.id === renderId ? updated : r))
            );
            queryClient.invalidateQueries({
                queryKey: templateRendersQueryKey(conversationId),
            });
        },
        onError: () => {
            toast.error("Couldn't retry the fill. Please try again.");
        },
    });
}
